import { useId, useMemo } from "react";
import {
  ghostBtnClass,
  mfgInlineInputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { sanitizeSlotLabelInput } from "@/lib/manufacturing/slotLabel";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type { VariantProportionalAddRule } from "@/lib/manufacturing/variantConfig";
import {
  ManufacturingComboboxPicker,
  type ComboboxOption,
} from "./ManufacturingComboboxPicker";

function emptyRule(): VariantProportionalAddRule {
  return {
    source_slot_label: "",
    quantity_factor: 1,
    material_id: null,
    material_code: "",
    material_name: "",
    slot_label: "",
  };
}

/** Catalog proportional-add rules — materialize to absolute deltas on apply. */
export function ManufacturingNamedVariantProportionalAddEditor({
  rules,
  materials,
  orgSlotLabels,
  onChange,
}: {
  rules: readonly VariantProportionalAddRule[];
  materials: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  onChange: (next: VariantProportionalAddRule[]) => void;
}) {
  const listId = useId();
  const rows = [...rules];
  const materialOptions = useMemo<ComboboxOption<MfgMaterial>[]>(
    () =>
      materials
        .filter((m) => m.is_active)
        .map((m) => ({
          key: m.id,
          label: m.code,
          sublabel: m.name,
          data: m,
        })),
    [materials],
  );

  function patch(index: number, patch: Partial<VariantProportionalAddRule>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-gray-500 dark:text-zinc-400">
        Proportional add: qty = parent source-slot qty × factor of a <em>different</em> material
        (e.g. lap siding over base siding). Original parent lines stay. Review quantities after
        apply.
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          No proportional add rules yet.
        </p>
      ) : null}
      {rows.map((row, index) => (
        <div
          key={`${listId}-${index}`}
          className="space-y-2 rounded-md border border-gray-100 p-2 dark:border-zinc-700"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
              Proportional add {index + 1}
            </span>
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="flex min-w-[140px] flex-1 flex-col gap-1">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
                Source slot
              </span>
              <input
                className={mfgInlineInputClass}
                list={`${listId}-source-${index}`}
                placeholder="e.g. siding"
                value={row.source_slot_label}
                onChange={(e) =>
                  patch(index, {
                    source_slot_label: sanitizeSlotLabelInput(e.target.value),
                  })
                }
              />
              <datalist id={`${listId}-source-${index}`}>
                {orgSlotLabels.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </label>
            <label className="flex min-w-[100px] flex-col gap-1">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
                Qty factor
              </span>
              <input
                className={mfgInlineInputClass}
                type="number"
                min={0.0001}
                step="any"
                value={row.quantity_factor}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  patch(index, {
                    quantity_factor: Number.isFinite(n) && n > 0 ? n : 1,
                  });
                }}
              />
            </label>
          </div>
          {row.material_use_id && !row.material_id ? (
            <div className="rounded-md border border-dashed border-gray-200 px-2 py-1.5 text-xs text-gray-700 dark:border-zinc-700 dark:text-zinc-300">
              Part use:{" "}
              <span className="font-medium">
                {row.material_code || row.material_use_id}
              </span>
              {row.material_name ? ` — ${row.material_name}` : ""}
            </div>
          ) : (
            <ManufacturingComboboxPicker<MfgMaterial>
              value={row.material_id ?? ""}
              options={materialOptions}
              placeholder="Select material to add…"
              onPick={(opt) =>
                patch(index, {
                  material_id: opt.data.id,
                  material_use_id: null,
                  material_code: opt.data.code,
                  material_name: opt.data.name,
                })
              }
            />
          )}
          <label className="flex min-w-[140px] flex-col gap-1">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
              Target slot (optional)
            </span>
            <input
              className={mfgInlineInputClass}
              list={`${listId}-target-${index}`}
              placeholder="Defaults to source slot"
              value={row.slot_label ?? ""}
              onChange={(e) =>
                patch(index, { slot_label: sanitizeSlotLabelInput(e.target.value) })
              }
            />
            <datalist id={`${listId}-target-${index}`}>
              {orgSlotLabels.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>
        </div>
      ))}
      <button
        type="button"
        className={ghostBtnClass}
        onClick={() => onChange([...rows, emptyRule()])}
      >
        + Add proportional rule
      </button>
    </div>
  );
}
